import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  DollarSign,
  ExternalLink,
  Gauge,
  Loader2,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  adoptionApi,
  type AdoptionFilter,
  type AdoptionOverview,
  type AdoptionUser,
  type MetricValidation,
  type OperationsMetrics,
  type QualityMetrics,
  type RunLeaderboard,
  type SessionsPage,
  type UsageMetrics,
} from '../services/adoptionService';

// --- window options ------------------------------------------------------
const DAY_OPTIONS = [7, 30, 90, 180] as const;

// Fallback when the backend omits metric_validation (pre-change build).
const EMPTY_VALIDATION: MetricValidation = {
  available: false,
  dataset_name: 'Metrics_Data_Validation',
  run_name: null,
  run_id: null,
  created_at: null,
  item_count: null,
  metrics: [],
};

// --- SmartCHG warm design tokens -----------------------------------------
// Matched to the existing KPI Cards / Performance Lens components so this
// dashboard reads as the same product. Plum is the brand/interactive colour;
// green/amber/red carry status only.
const CHG = {
  plum: '#8D3165',
  plumDeep: '#6E3450',
  plumTint: '#F5E9EE', // soft plum tint for selected/highlighted states
  ink: '#1F1A20', // warm charcoal (primary text)
  inkSoft: '#3F3338', // secondary value ink
  muted: '#7B6B72', // muted warm grey (secondary text / icons)
  faint: '#A8989F', // tertiary text
  border: '#EDE6E1', // warm neutral border
  divider: '#EDE5D8', // beige divider (grid gaps / hairlines)
  surface: '#FFFFFF',
  surfaceWarm: '#FAF7F1', // warm-white filled surface
  good: '#3F7D4F', // muted green
  warn: '#B5842E', // muted amber
  bad: '#B23A48', // muted red
} as const;

// Shared card treatment (radius, warm border, very subtle elevation).
const CARD =
  'rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]';

// Bars use plum as the primary series; status bars use semantic colours.
const HUE = CHG.plum;

function statusColor(rate: number | null | undefined): string {
  if (rate == null) return CHG.faint;
  if (rate >= 0.9) return CHG.good;
  if (rate >= 0.7) return CHG.warn;
  return CHG.bad;
}

// Semantic status → { label, colour, pill classes }. Colour is never the only
// signal — every pill carries a word too.
type StatusLevel = 'healthy' | 'watchout' | 'below' | 'none';
function statusLevel(rate: number | null | undefined): StatusLevel {
  if (rate == null) return 'none';
  if (rate >= 0.9) return 'healthy';
  if (rate >= 0.7) return 'watchout';
  return 'below';
}
const STATUS_PILL: Record<Exclude<StatusLevel, 'none'>, { label: string; cls: string }> = {
  healthy: { label: 'Healthy', cls: 'border-[#3F7D4F]/30 bg-[#3F7D4F]/10 text-[#3F7D4F]' },
  watchout: { label: 'Watchout', cls: 'border-[#B5842E]/30 bg-[#B5842E]/12 text-[#8A6420]' },
  below: { label: 'Below target', cls: 'border-[#B23A48]/30 bg-[#B23A48]/10 text-[#B23A48]' },
};

// --- formatting ----------------------------------------------------------
const pct = (v: number | null | undefined, digits = 1): string =>
  v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
const num = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString();
const dec = (v: number | null | undefined, d = 1): string =>
  v == null ? '—' : v.toFixed(d);
const usd = (v: number | null | undefined): string =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const ms = (v: number | null | undefined): string => {
  if (v == null) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(2)}s` : `${Math.round(v)}ms`;
};
// Human-friendly duration from seconds (e.g. "3m 12s", "1h 4m").
const dur = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  if (s <= 0) return '0s';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

function humanError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const r = err as { response?: { data?: { detail?: unknown }; status?: number }; message?: string };
    const detail = r.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (r.response?.status === 403) return 'Super-admin access is required to view adoption metrics.';
    if (r.message) return r.message;
  }
  return 'Unable to load adoption metrics.';
}

// --- small presentational pieces ----------------------------------------

// Status pill — tinted background + border + a word (never colour alone).
const StatusPill: React.FC<{ level: StatusLevel; label?: string }> = ({ level, label }) => {
  if (level === 'none') return null;
  const s = STATUS_PILL[level];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums ${s.cls}`}
    >
      {label ?? s.label}
    </span>
  );
};

interface TileProps {
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  sub?: string;
  status?: StatusLevel; // renders a status pill; value stays charcoal
  className?: string;
}
// KPI cell — matches the SmartCHG KPICard: uppercase tracked label, large
// light display value in warm charcoal, restrained line icon, optional pill.
// Designed to sit inside a divider grid (transparent bg), not float alone.
const Tile: React.FC<TileProps> = ({ label, value, icon: Icon, sub, status, className }) => (
  <div className={`flex min-w-0 flex-col bg-white px-5 py-4 ${className ?? ''}`}>
    <div className="flex items-start justify-between gap-2">
      <span className="break-words text-[11px] font-semibold uppercase leading-tight tracking-[0.12em] text-[#7B6B72]">
        {label}
      </span>
      <Icon className="h-4 w-4 shrink-0 text-[#A8989F]" strokeWidth={1.75} aria-hidden />
    </div>
    <div className="mt-2 font-display text-[clamp(1.75rem,2.6vw,2.25rem)] font-light leading-[1.05] tabular-nums text-[#1F1A20]">
      {value}
    </div>
    <div className="mt-1.5 flex items-center gap-2">
      {status && status !== 'none' && <StatusPill level={status} />}
      {sub && <span className="truncate text-[12px] text-[#7B6B72]">{sub}</span>}
    </div>
  </div>
);

// Section container — SmartCHG chrome: warm-white surface, thin border, subtle
// elevation, an uppercase eyebrow header with optional supporting text, and
// generous internal padding. Actions align to the right of the header.
const Section: React.FC<{
  title: string;
  description?: string;
  actions?: React.ReactNode;
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ title, description, actions, bodyClassName, children }) => (
  <section className={`${CARD} mt-5 first:mt-0`}>
    <header className="flex items-start justify-between gap-4 border-b border-[#EDE5D8] px-4 pb-3 pt-4 md:px-5">
      <div className="min-w-0">
        <h2 className="truncate text-[12px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">
          {title}
        </h2>
        {description && (
          <p className="mt-0.5 text-[13px] leading-snug text-[#7B6B72]">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
    <div className={`px-4 py-4 md:px-5 ${bodyClassName ?? ''}`}>{children}</div>
  </section>
);

// A lightweight inner module (used inside a section for the two-column
// chart/table layouts and the quality lenses). Warm hairline, no heavy nesting.
const Module: React.FC<{ title: string; className?: string; children: React.ReactNode }> = ({
  title,
  className,
  children,
}) => (
  <div className={`min-w-0 ${className ?? ''}`}>
    <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">
      {title}
    </h3>
    {children}
  </div>
);

// KPI divider grid — white Tile cells over a beige backdrop so the 1px gaps
// read as warm hairline dividers (the SmartCHG KPI Cards pattern). One cohesive
// rounded unit inside the section body rather than floating tiles.
const KpiGrid: React.FC<{ cols: string; children: React.ReactNode }> = ({ cols, children }) => (
  <div className={`grid ${cols} gap-px overflow-hidden rounded-[12px] bg-[#EDE5D8]`}>
    {children}
  </div>
);

// Shared table cell treatments — understated horizontal separators, uppercase
// muted headers, warm plum hover.
const TH = 'py-2.5 px-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#7B6B72]';
const TD = 'py-2.5 px-3 text-[13px] text-[#1F1A20]';
const TR = 'border-b border-[#EDE5D8]/70 transition-colors last:border-0 hover:bg-[#F5E9EE]/40';

// Restrained secondary pager.
const Pager: React.FC<{
  page: number;
  hasMore: boolean;
  loading: boolean;
  onPage: (p: number) => void;
  caption: string;
}> = ({ page, hasMore, loading, onPage, caption }) => (
  <div className="mt-3 flex items-center justify-between text-[12px]">
    <span className="text-[#7B6B72]">{caption}</span>
    <div className="flex items-center gap-2">
      <button
        onClick={() => onPage(page - 1)}
        disabled={page <= 1 || loading}
        className="rounded-full border border-[#EDE6E1] bg-white px-3 py-1 text-[13px] text-[#3F3338] transition-colors hover:bg-[#FAF7F1] disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#8D3165]/25"
      >
        Prev
      </button>
      <button
        onClick={() => onPage(page + 1)}
        disabled={!hasMore || loading}
        className="rounded-full border border-[#EDE6E1] bg-white px-3 py-1 text-[13px] text-[#3F3338] transition-colors hover:bg-[#FAF7F1] disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#8D3165]/25"
      >
        Next
      </button>
    </div>
  </div>
);

// Combined satisfaction lens: neutral charcoal headline + status pill, a slim
// segmented proportion bar (softened green/red), and compact labelled counts.
const SatisfactionTile: React.FC<{
  rate: number | null;
  up: number;
  down: number;
  className?: string;
}> = ({ rate, up, down, className }) => {
  const total = up + down;
  const upPct = total ? (up / total) * 100 : 0;
  return (
    <div className={`min-w-0 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">
          User feedback
        </h3>
        <span className="text-[12px] tabular-nums text-[#A8989F]">
          {total ? `${num(total)} ratings` : 'No ratings yet'}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2.5">
        <span className="font-display text-[2rem] font-light leading-none tabular-nums text-[#1F1A20]">
          {pct(rate)}
        </span>
        <StatusPill level={statusLevel(rate)} />
      </div>
      {/* Proportion bar */}
      <div
        className="mt-3 flex h-2 overflow-hidden rounded-full bg-[#EDE5D8]"
        role="img"
        aria-label={`${num(up)} positive, ${num(down)} negative`}
      >
        {total > 0 && (
          <>
            <div style={{ width: `${upPct}%`, backgroundColor: CHG.good }} />
            <div style={{ width: `${100 - upPct}%`, backgroundColor: CHG.bad }} />
          </>
        )}
      </div>
      <div className="mt-3 flex items-center gap-5 text-[13px]">
        <span className="flex items-center gap-1.5">
          <ThumbsUp className="h-4 w-4" strokeWidth={1.75} style={{ color: CHG.good }} aria-hidden />
          <span className="font-medium tabular-nums text-[#1F1A20]">{num(up)}</span>
          <span className="text-[#7B6B72]">positive</span>
        </span>
        <span className="flex items-center gap-1.5">
          <ThumbsDown className="h-4 w-4" strokeWidth={1.75} style={{ color: CHG.bad }} aria-hidden />
          <span className="font-medium tabular-nums text-[#1F1A20]">{num(down)}</span>
          <span className="text-[#7B6B72]">negative</span>
        </span>
      </div>
    </div>
  );
};

interface BarDatum {
  label: string;
  value: number | null;
  color?: string;
}

const CategoryTick = ({ x = 0, y = 0, payload }: { x?: number; y?: number; payload?: { value?: unknown } }) => {
  const label = String(payload?.value ?? '');
  return (
    <text
      x={x - 8}
      y={y}
      dy={4}
      textAnchor="end"
      fill={CHG.ink}
      fontSize={12}
    >
      {label.length > 18 ? `${label.slice(0, 17)}…` : label}
    </text>
  );
};

// Horizontal bar chart for a single measure across categories. Warm-neutral
// axes/gridlines, plum (or semantic) bars, charcoal tabular labels, and a
// SmartCHG-surface tooltip. Paired with a table so identity is never colour-only.
const HBars: React.FC<{ data: BarDatum[]; format: (v: number | null) => string; domainMax?: number }> = ({
  data,
  format,
  domainMax,
}) => {
  if (!data.length) return <p className="text-[13px] text-[#7B6B72]">No data in this window.</p>;
  const rows = data.map((d) => ({ ...d, plot: d.value ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={Math.max(132, rows.length * 30)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 34, bottom: 0, left: 0 }}>
        <CartesianGrid horizontal={false} stroke={CHG.divider} />
        <XAxis
          type="number"
          domain={[0, domainMax ?? 'auto']}
          tick={{ fontSize: 11, fill: CHG.muted }}
          tickFormatter={(v) => format(v as number)}
          axisLine={{ stroke: CHG.divider }}
          tickLine={{ stroke: CHG.divider }}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={156}
          interval={0}
          tick={<CategoryTick />}
          axisLine={{ stroke: CHG.divider }}
          tickLine={false}
        />
        <RTooltip
          cursor={{ fill: 'rgba(141,49,106,0.06)' }}
          contentStyle={{
            background: CHG.surface,
            border: `1px solid ${CHG.border}`,
            borderRadius: 10,
            fontSize: 12,
            color: CHG.ink,
            boxShadow: '0 4px 6px -1px rgba(31,26,32,.06),0 12px 28px -10px rgba(110,52,80,.18)',
          }}
          labelStyle={{ color: CHG.ink }}
          formatter={(v: number) => [format(v), '']}
        />
        <Bar dataKey="plot" radius={[0, 4, 4, 0]} barSize={14}>
          {rows.map((r, i) => (
            <Cell key={i} fill={r.color ?? HUE} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Metric validation lens — latest run of the Langfuse eval dataset: overall
// pass rate + per-metric pass-rate bars and a paired table. Falls back to an
// intentional SmartCHG empty state with a plum secondary action.
const MetricValidationCard: React.FC<{
  mv: MetricValidation;
  bars: BarDatum[];
  langfuseAvailable: boolean;
}> = ({ mv, bars, langfuseAvailable }) => {
  const totalItems = mv.metrics.reduce((a, m) => a + (m.total ?? 0), 0);
  const totalPassed = mv.metrics.reduce((a, m) => a + (m.passed ?? 0), 0);
  const overall = totalItems ? totalPassed / totalItems : null;
  const hasData = langfuseAvailable && mv.available && mv.metrics.length > 0;

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">
          Metric validation
        </h3>
        {hasData && overall != null && (
          <div className="flex items-center gap-2">
            <span className="font-display text-[1.5rem] font-light leading-none tabular-nums text-[#1F1A20]">
              {pct(overall)}
            </span>
            <StatusPill level={statusLevel(overall)} />
          </div>
        )}
      </div>

      {!hasData ? (
        // Intentional empty state — line icon, concise copy, plum action.
        <div className="mt-3 flex flex-col items-center gap-2 rounded-[12px] border border-dashed border-[#EDE6E1] bg-[#FAF7F1] px-5 py-8 text-center">
          <ShieldCheck className="h-6 w-6 text-[#B7A9AF]" strokeWidth={1.5} aria-hidden />
          <p className="text-[13px] font-medium text-[#1F1A20]">
            {langfuseAvailable ? 'No validation runs yet' : 'Validation unavailable'}
          </p>
          <p className="max-w-sm text-[12px] leading-snug text-[#7B6B72]">
            {langfuseAvailable
              ? 'Run the model against your ground-truth set to see how closely its numbers match.'
              : 'Model validation requires observability to be enabled for this workspace.'}
          </p>
          {langfuseAvailable && (
            <button
              type="button"
              className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-[#EDE6E1] bg-white px-3 py-1.5 text-[13px] font-medium text-[#6E3450] transition-colors hover:bg-[#F5E9EE] focus:outline-none focus:ring-2 focus:ring-[#8D3165]/30"
            >
              Run validation
            </button>
          )}
          {mv.dataset_name && (
            <p className="mt-1 text-[11px] text-[#A8989F]">Dataset · {mv.dataset_name}</p>
          )}
        </div>
      ) : (
        <>
          <p className="mt-1 text-[12px] text-[#A8989F]">
            {mv.run_name ? `Latest run · ${fmtDate(mv.created_at)}` : ''}
            {mv.item_count != null ? ` · ${num(mv.item_count)} items` : ''}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <HBars data={bars} format={(v) => pct(v)} domainMax={1} />
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-[#EDE5D8] text-left text-[11px] uppercase tracking-[0.06em] text-[#7B6B72]">
                    <th className="py-2 pr-2 font-semibold">Metric</th>
                    <th className="py-2 px-2 text-right font-semibold">Mean</th>
                    <th className="py-2 px-2 text-right font-semibold">Passed</th>
                    <th className="py-2 pl-2 text-right font-semibold">Pass rate</th>
                  </tr>
                </thead>
                <tbody>
                  {mv.metrics.map((m) => (
                    <tr
                      key={m.name}
                      className="border-b border-[#EDE5D8]/70 transition-colors last:border-0 hover:bg-[#F5E9EE]/40"
                    >
                      <td className="py-2 pr-2 text-[#1F1A20]">{m.name}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#3F3338]">{dec(m.mean, 3)}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#7B6B72]">
                        {m.total != null ? `${num(m.passed)} / ${num(m.total)}` : '—'}
                      </td>
                      <td
                        className="py-2 pl-2 text-right tabular-nums font-medium"
                        style={{ color: statusColor(m.pass_rate) }}
                      >
                        {pct(m.pass_rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function userLabel(u: AdoptionUser): string {
  const name = u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} · ${u.email}` : u.email;
}

interface MSOption {
  value: string;
  label: string;
}
// Compact checkbox dropdown for a multi-select facet. Closes on outside
// click; the trigger summarizes the current selection.
const MultiSelect: React.FC<{
  label: string;
  options: MSOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  emptyText: string;
  searchable?: boolean;
}> = ({ label, options, selected, onChange, emptyText, searchable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const shown = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;
  const summary =
    selected.length === 0
      ? emptyText
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? `1 ${label}`
        : `${selected.length} ${label}`;

  const active = selected.length > 0;
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex h-9 max-w-[15rem] items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors focus:outline-none focus:ring-2 focus:ring-[#8D3165]/30 ${
          active
            ? 'border-[#8D3165]/30 bg-[#F5E9EE] text-[#6E3450]'
            : 'border-[#EDE6E1] bg-white text-[#3F3338] hover:bg-[#FAF7F1]'
        }`}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" strokeWidth={1.75} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1.5 max-h-72 w-64 overflow-auto rounded-[12px] border border-[#EDE6E1] bg-white p-1.5 shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">
              {label}
            </span>
            {active && (
              <button
                onClick={() => onChange([])}
                className="text-[12px] font-medium text-[#6E3450] hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          {searchable && (
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="mb-1 h-8 w-full rounded-lg border border-[#EDE6E1] bg-[#FAF7F1] px-2 text-[13px] text-[#1F1A20] placeholder:text-[#A8989F] focus:outline-none focus:ring-2 focus:ring-[#8D3165]/25"
            />
          )}
          {shown.length === 0 ? (
            <p className="px-2 py-2 text-[12px] text-[#7B6B72]">No matches.</p>
          ) : (
            shown.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-[#1F1A20] hover:bg-[#F5E9EE]/50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  className="accent-[#8D3165]"
                />
                <span className="truncate">{o.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Run leaderboard — users ranked by total runs, platform-wide, paginated
// (top 5 by default). Manages its own fetch + page state so paging doesn't
// reload the dashboard. Clicking a user adds them to the top-level filter.
const RunLeaderboardCard: React.FC<{
  days: number;
  filter: AdoptionFilter;
  onPickUser: (id: string) => void;
}> = ({ days, filter, onPickUser }) => {
  const [data, setData] = useState<RunLeaderboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        setData(await adoptionApi.runLeaderboard(days, filter, page, 5));
      } catch (err) {
        setError(humanError(err));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [days, filter],
  );

  useEffect(() => {
    void loadPage(1);
  }, [loadPage]);

  const startRank = data ? (data.page - 1) * data.limit : 0;
  const top = data?.rows[0]?.run_count ?? 0; // for the relative meter

  return (
    <Module title="Most active users">
      {error ? (
        // Degrade quietly — an older backend without this route 404s here;
        // that shouldn't read as a hard failure next to the working modules.
        <p className="text-[13px] text-[#7B6B72]">Leaderboard unavailable in this window.</p>
      ) : loading && !data ? (
        <div className="flex items-center py-4 text-[13px] text-[#7B6B72]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : data && data.rows.length > 0 ? (
        <>
          <ol className="flex flex-col">
            {data.rows.map((r, i) => {
              const rank = startRank + i + 1;
              const label = r.user_label ?? r.user_id;
              const width = top > 0 ? Math.max(4, (r.run_count / top) * 100) : 0;
              return (
                <li
                  key={r.user_id}
                  className="flex items-center gap-3 border-b border-[#EDE5D8]/70 py-2 last:border-0"
                >
                  <span className="w-5 shrink-0 text-[13px] font-semibold tabular-nums text-[#A8989F]">
                    {rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => onPickUser(r.user_id)}
                      className="block max-w-full truncate text-left text-[13px] font-medium text-[#6E3450] hover:underline"
                      title={`Add ${label} to the filter`}
                    >
                      {label}
                    </button>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#EDE5D8]">
                      <div style={{ width: `${width}%`, backgroundColor: CHG.plum }} className="h-full" />
                    </div>
                  </div>
                  <span className="shrink-0 text-[13px] font-medium tabular-nums text-[#1F1A20]">
                    {num(r.run_count)}
                  </span>
                </li>
              );
            })}
          </ol>
          <Pager
            page={data.page}
            hasMore={data.has_more}
            loading={loading}
            onPage={(p) => void loadPage(p)}
            caption={`Top ${data.limit} · ${num(data.total_users)} users with runs`}
          />
        </>
      ) : (
        <p className="text-[13px] text-[#7B6B72]">No runs in this window.</p>
      )}
    </Module>
  );
};

// Bottom "Sessions" list — Langfuse-sourced (traces grouped by session),
// driven by the dashboard's top filters (window + department/user). Paginated;
// each row shows the owner (label resolved from the users map) and can add
// that user to the top-level filter. Manages its own fetch + page state.
const SessionsSection: React.FC<{
  days: number;
  filter: AdoptionFilter;
  usersById: Map<string, AdoptionUser>;
  onPickUser: (id: string) => void;
  totalCostUsd?: number | null;
  totalCostLoading: boolean;
}> = ({ days, filter, usersById, onPickUser, totalCostUsd, totalCostLoading }) => {
  const [data, setData] = useState<SessionsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        setData(await adoptionApi.sessions(days, filter, page));
      } catch (err) {
        setError(humanError(err));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [days, filter],
  );

  // Reload page 1 whenever the window or filter changes.
  useEffect(() => {
    void loadPage(1);
  }, [loadPage]);

  const labelFor = (uid: string | null): string => {
    if (!uid) return 'unknown';
    const u = usersById.get(uid);
    return u ? userLabel(u) : uid;
  };

  return (
    <Section
      title="Sessions"
      description="Recent Langfuse sessions over the window. Click a user to add them to the filter."
    >
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-[12px] border border-[#B23A48]/25 bg-[#B23A48]/8 px-4 py-3 text-[13px] text-[#B23A48]">
          <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center py-6 text-[13px] text-[#7B6B72]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading sessions…
        </div>
      ) : data ? (
        <>
          <KpiGrid cols="grid-cols-2 md:grid-cols-4">
            <Tile label="Sessions" value={num(data.session_count)} icon={Activity} />
            <Tile
              label="Avg session length"
              value={dur(data.avg_duration_seconds)}
              sub="first → last trace"
              icon={Clock}
            />
            <Tile
              label="Avg turns / session"
              value={dec(data.avg_traces_per_session)}
              sub={`${num(data.total_traces)} traces`}
              icon={MessageSquare}
            />
            <Tile
              label="Total cost"
              value={
                totalCostLoading ? (
                  <Loader2
                    className="h-8 w-8 animate-spin text-[#7B6B72]"
                    aria-label="Loading total cost"
                  />
                ) : (
                  usd(totalCostUsd)
                )
              }
              icon={DollarSign}
            />
          </KpiGrid>

          <div className="mt-5">
            {data.sessions.length === 0 ? (
              <p className="text-[13px] text-[#7B6B72]">No sessions in this window.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#EDE5D8] text-left">
                        <th className={TH}>User</th>
                        <th className={TH}>Session</th>
                        <th className={TH}>Started</th>
                        <th className={`${TH} text-right`}>Length</th>
                        <th className={`${TH} text-right`}>Turns</th>
                        <th className={`${TH} text-right`}>Cost</th>
                        <th className={`${TH} text-right`}>Langfuse</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sessions.map((s) => (
                        <tr key={s.session_id} className={TR}>
                          <td className={TD}>
                            {s.user_id ? (
                              <button
                                onClick={() => onPickUser(s.user_id!)}
                                className="max-w-[12rem] truncate text-left font-medium text-[#6E3450] hover:underline"
                                title={`Add ${labelFor(s.user_id)} to the filter`}
                              >
                                {labelFor(s.user_id)}
                              </button>
                            ) : (
                              <span className="text-[12px] text-[#A8989F]">—</span>
                            )}
                          </td>
                          <td className={`${TD} max-w-[18rem] truncate`} title={s.name ?? s.session_id}>
                            {s.name || (
                              <span className="font-mono text-[12px] text-[#A8989F]">{s.session_id}</span>
                            )}
                          </td>
                          <td className={`${TD} text-[#7B6B72]`}>{fmtDate(s.started_at)}</td>
                          <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                            {dur(s.duration_seconds)}
                          </td>
                          <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                            {num(s.trace_count)}
                          </td>
                          <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                            {usd(s.total_cost_usd)}
                          </td>
                          <td className={`${TD} text-right`}>
                            {s.langfuse_url ? (
                              <a
                                href={s.langfuse_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 font-medium text-[#6E3450] hover:underline"
                              >
                                View <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                              </a>
                            ) : (
                              <span className="text-[12px] text-[#A8989F]">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pager
                  page={data.page}
                  hasMore={data.has_more}
                  loading={loading}
                  onPage={(p) => void loadPage(p)}
                  caption={`Page ${data.page} · showing ${data.sessions.length} of ${num(
                    data.session_count,
                  )}${data.approximate ? ' (approx — narrow the window for exact counts)' : ''}`}
                />
              </>
            )}
            {!data.langfuse_available && (
              <p className="mt-2 text-[12px] text-[#A8989F]">
                Sessions are sourced from Langfuse — enable observability to populate this list.
              </p>
            )}
          </div>
        </>
      ) : null}
    </Section>
  );
};

// =========================================================================
export default function AdoptionDashboardPage(): React.ReactElement {
  const [days, setDays] = useState<number>(30);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [users, setUsers] = useState<AdoptionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<AdoptionOverview | null>(null);
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [quality, setQuality] = useState<QualityMetrics | null>(null);
  const [ops, setOps] = useState<OperationsMetrics | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState<string | null>(null);
  const loadGeneration = useRef(0);

  // User list for the top filter (loaded once).
  useEffect(() => {
    let cancelled = false;
    adoptionApi
      .users()
      .then((list) => {
        if (cancelled) return;
        setUsers([...list].sort((a, b) => userLabel(a).localeCompare(userLabel(b))));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const usersById = useMemo(
    () => new Map(users.map((u) => [u.user_id, u])),
    [users],
  );

  // Departments come from the resolved department on every user.
  const departmentOptions = useMemo<MSOption[]>(() => {
    const set = new Set<string>();
    users.forEach((u) => {
      const department = u.department?.trim();
      if (department) set.add(department);
    });
    return [...set].sort().map((department) => ({ value: department, label: department }));
  }, [users]);

  // "Department narrows the user list": when departments are selected, the user dropdown
  // shows only users in one of them. Users no longer visible are dropped
  // from the current selection to keep the filter coherent.
  const userOptions = useMemo<MSOption[]>(() => {
    const pool = selectedDepartments.length
      ? users.filter((u) => selectedDepartments.includes(u.department))
      : users;
    return pool.map((u) => ({ value: u.user_id, label: userLabel(u) }));
  }, [users, selectedDepartments]);

  useEffect(() => {
    const visible = new Set(userOptions.map((o) => o.value));
    setSelectedUserIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [userOptions]);

  // The effective filter sent to every scoped endpoint.
  const filter = useMemo<AdoptionFilter>(
    () => ({ departments: selectedDepartments, userIds: selectedUserIds }),
    [selectedDepartments, selectedUserIds],
  );
  const isFiltered = selectedDepartments.length > 0 || selectedUserIds.length > 0;

  const load = useCallback(async (windowDays: number, f: AdoptionFilter) => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    setOps(null);
    setOpsLoading(true);
    setOpsError(null);
    void adoptionApi
      .operations(windowDays, f)
      .then((result) => {
        if (generation === loadGeneration.current) setOps(result);
      })
      .catch((err) => {
        if (generation === loadGeneration.current) setOpsError(humanError(err));
      })
      .finally(() => {
        if (generation === loadGeneration.current) setOpsLoading(false);
      });
    try {
      // Operations loads independently because its model-cost aggregation can
      // take longer than the primary dashboard metrics.
      const [o, u, q] = await Promise.all([
        adoptionApi.overview(windowDays, f),
        adoptionApi.usage(windowDays),
        adoptionApi.quality(windowDays, f),
      ]);
      if (generation !== loadGeneration.current) return;
      setOverview(o);
      setUsage(u);
      setQuality(q);
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      setError(humanError(err));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days, filter);
  }, [days, filter, load]);

  // Add a user to the filter from a session row (dedup).
  const addUser = useCallback((id: string) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const departmentBars = useMemo<BarDatum[]>(
    () => (usage?.by_department ?? []).map((r) => ({ label: r.department, value: r.active_pct })),
    [usage],
  );
  const officeLocationRows = useMemo(
    () => usage?.by_office_location ?? [],
    [usage],
  );
  const officeLocationBars = useMemo<BarDatum[]>(
    () =>
      officeLocationRows
        .slice(0, 10)
        .map((d) => ({ label: d.office_location, value: d.active_pct })),
    [officeLocationRows],
  );
  const controlBars = useMemo<BarDatum[]>(
    () =>
      (ops?.node_controls ?? []).map((c) => ({
        label: c.node_name,
        value: c.pass_rate,
        color: statusColor(c.pass_rate),
      })),
    [ops],
  );
  const costBars = useMemo<BarDatum[]>(
    () => (ops?.by_model ?? []).map((m) => ({ label: m.model, value: m.total_cost_usd })),
    [ops],
  );
  const validationBars = useMemo<BarDatum[]>(
    () =>
      (quality?.metric_validation?.metrics ?? []).map((m) => ({
        label: m.name,
        value: m.pass_rate,
        color: statusColor(m.pass_rate),
      })),
    [quality],
  );
  return (
    <div className="h-full overflow-y-auto bg-[#FBF7F1] px-4 py-5 md:px-6 md:py-6">
      <div className="mx-auto max-w-[1440px]">
        {/* Page header — integrated (not carded) */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-[clamp(1.75rem,3vw,2rem)] font-semibold tracking-tight text-[#1F1A20]">
              Adoption &amp; Health
            </h1>
            <p className="mt-1 text-[14px] leading-snug text-[#7B6B72]">
              {isFiltered
                ? 'Filtered by department / user — population metrics stay platform-wide.'
                : 'Usage, outcome quality, and operational reliability across the platform.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Cross-filters — department narrows the user list; both scope the
                dashboard (except population metrics). */}
            <MultiSelect
              label="departments"
              options={departmentOptions}
              selected={selectedDepartments}
              onChange={setSelectedDepartments}
              emptyText="All departments"
            />
            <MultiSelect
              label="users"
              options={userOptions}
              selected={selectedUserIds}
              onChange={setSelectedUserIds}
              emptyText="All users"
              searchable
            />
            {/* Segmented period selector — selected = solid plum. */}
            <div className="flex rounded-full border border-[#EDE6E1] bg-white p-0.5">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  aria-pressed={days === d}
                  className={`rounded-full px-3 py-1 text-[13px] font-medium tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-[#8D3165]/30 ${
                    days === d
                      ? 'bg-[#8D3165] text-white'
                      : 'text-[#7B6B72] hover:text-[#1F1A20]'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              onClick={() => void load(days, filter)}
              disabled={loading || opsLoading}
              aria-label="Refresh"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#EDE6E1] bg-white text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#6E3450] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#8D3165]/30"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading || opsLoading ? 'animate-spin' : ''}`}
                strokeWidth={1.75}
              />
            </button>
          </div>
        </div>
        {isFiltered && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {selectedDepartments.map((department) => (
              <span
                key={`d-${department}`}
                className="inline-flex items-center gap-1 rounded-full border border-[#8D3165]/20 bg-[#F5E9EE] px-2.5 py-0.5 text-[12px] font-medium text-[#6E3450]"
              >
                department: {department}
                <button
                  onClick={() => setSelectedDepartments((p) => p.filter((x) => x !== department))}
                  aria-label={`Remove ${department}`}
                  className="text-[#6E3450]/70 hover:text-[#6E3450]"
                >
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </span>
            ))}
            {selectedUserIds.map((id) => (
              <span
                key={`u-${id}`}
                className="inline-flex items-center gap-1 rounded-full border border-[#8D3165]/20 bg-[#F5E9EE] px-2.5 py-0.5 text-[12px] font-medium text-[#6E3450]"
              >
                {usersById.get(id) ? userLabel(usersById.get(id)!) : id}
                <button
                  onClick={() => setSelectedUserIds((p) => p.filter((x) => x !== id))}
                  aria-label="Remove user"
                  className="text-[#6E3450]/70 hover:text-[#6E3450]"
                >
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </span>
            ))}
            <button
              onClick={() => {
                setSelectedDepartments([]);
                setSelectedUserIds([]);
              }}
              className="text-[12px] font-medium text-[#7B6B72] hover:text-[#6E3450] hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-center gap-2 rounded-[12px] border border-[#B23A48]/25 bg-[#B23A48]/8 px-4 py-3 text-[13px] text-[#B23A48]">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
            {error}
          </div>
        )}

        {loading && !overview ? (
          <div className="flex h-64 items-center justify-center text-[13px] text-[#7B6B72]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading metrics…
          </div>
        ) : (
          <>
            {/* Overview */}
            {overview && (
              <Section title="Overview">
                <KpiGrid cols="grid-cols-1 sm:grid-cols-3 lg:grid-cols-6">
                  <Tile
                    label="Active users"
                    value={pct(overview.active_pct)}
                    sub={`${num(overview.active_users)} / ${num(overview.total_users)}`}
                    icon={Users}
                  />
                  <Tile label="Total runs" value={num(overview.total_runs)} icon={Activity} />
                  <Tile
                    label="Repeat rate"
                    value={pct(overview.repeat_usage_rate)}
                    sub="of active users"
                    icon={RefreshCw}
                  />
                  <Tile
                    label="Satisfaction"
                    value={pct(overview.satisfaction_rate)}
                    icon={ThumbsUp}
                    status={statusLevel(overview.satisfaction_rate)}
                  />
                  <Tile
                    label="Run success"
                    value={pct(overview.success_rate)}
                    icon={CheckCircle2}
                    status={statusLevel(overview.success_rate)}
                  />
                  <Tile
                    label="Spend"
                    value={
                      opsLoading ? (
                        <Loader2
                          className="h-8 w-8 animate-spin text-[#7B6B72]"
                          aria-label="Loading spend"
                        />
                      ) : (
                        usd(ops?.total_cost_usd)
                      )
                    }
                    icon={DollarSign}
                  />
                </KpiGrid>
              </Section>
            )}

            {/* Adoption */}
            {usage && (
              <Section
                title="Adoption & user behaviour"
                description="Who is using the platform, how often, and whether they come back."
              >
                <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[260px_minmax(0,1fr)_minmax(280px,0.9fr)]">
                  <KpiGrid cols="grid-cols-1">
                    <Tile
                      label="Runs / active user"
                      value={dec(usage.runs_per_active_user)}
                      sub={`${num(usage.total_runs)} runs · ${num(usage.active_users)} active`}
                      icon={Activity}
                    />
                    <Tile
                      label="Repeat users"
                      value={num(usage.repeat_users)}
                      sub={`2+ days · ${pct(usage.repeat_usage_rate)} of active`}
                      icon={RefreshCw}
                    />
                  </KpiGrid>

                  <Module title="% active by department">
                    <HBars data={departmentBars} format={(v) => pct(v)} domainMax={1} />
                  </Module>

                  <RunLeaderboardCard days={days} filter={filter} onPickUser={addUser} />
                </div>

                <div className="mt-5 border-t border-[#EDE5D8] pt-5">
                  <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
                    <Module title="Breakdown by department">
                      <div className="max-h-[340px] overflow-auto pr-1">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-[#EDE5D8] text-left">
                              <th className={TH}>Department</th>
                              <th className={`${TH} text-right`}>Active</th>
                              <th className={`${TH} text-right`}>Total</th>
                              <th className={`${TH} text-right`}>% Active</th>
                              <th className={`${TH} text-right`}>Runs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage.by_department.map((r) => (
                              <tr key={r.department} className={TR}>
                                <td className={TD}>{r.department}</td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {num(r.active_users)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#7B6B72]`}>
                                  {num(r.total_users)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {pct(r.active_pct)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {num(r.runs)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Module>

                    <Module title="% active by office location">
                      <HBars data={officeLocationBars} format={(v) => pct(v)} domainMax={1} />
                      {officeLocationRows.length > officeLocationBars.length && (
                        <p className="mt-2 text-[12px] text-[#A8989F]">
                          Showing top {officeLocationBars.length} office locations by active users.
                        </p>
                      )}
                    </Module>
                  </div>
                </div>

                <div className="mt-5 border-t border-[#EDE5D8] pt-5">
                  <Module title="Breakdown by office location">
                    {officeLocationRows.length === 0 ? (
                      <p className="text-[13px] text-[#7B6B72]">No office-location data in this window.</p>
                    ) : (
                      <div className="max-h-[320px] overflow-auto pr-1">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-[#EDE5D8] text-left">
                              <th className={TH}>Office location</th>
                              <th className={`${TH} text-right`}>Active</th>
                              <th className={`${TH} text-right`}>Total</th>
                              <th className={`${TH} text-right`}>% Active</th>
                              <th className={`${TH} text-right`}>Runs</th>
                              <th className={`${TH} text-right`}>Runs / Active</th>
                            </tr>
                          </thead>
                          <tbody>
                            {officeLocationRows.map((d) => (
                              <tr key={d.office_location} className={TR}>
                                <td className={`${TD} max-w-[340px] truncate`} title={d.office_location}>
                                  {d.office_location}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {num(d.active_users)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#7B6B72]`}>
                                  {num(d.total_users)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {pct(d.active_pct)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {num(d.runs)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {dec(d.runs_per_active_user)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Module>
                </div>
              </Section>
            )}

            {/* Outcome quality */}
            {quality && (
              <Section
                title="Outcome quality"
                description="Three independent lenses on whether the output can be trusted: humans, ground truth, and a model judge."
              >
                {/* Three lenses, one system, separated by warm hairlines:
                    (1) user feedback, (2) model judgement, (3) metric validation. */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-[#EDE5D8]">
                  {/* Lens 1 — human feedback */}
                  <div className="lg:pr-6">
                    <SatisfactionTile
                      rate={quality.satisfaction_rate}
                      up={quality.thumbs_up}
                      down={quality.thumbs_down}
                    />
                    <div className="mt-4 border-t border-[#EDE5D8] pt-4">
                      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">
                        By department
                      </h4>
                      {quality.feedback_by_department.length === 0 ? (
                        <p className="text-[13px] text-[#7B6B72]">No feedback in this window.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#EDE5D8] text-left">
                                <th className={TH}>Department</th>
                                <th className={`${TH} text-right`}>
                                  <ThumbsUp
                                    className="ml-auto h-3.5 w-3.5"
                                    strokeWidth={1.75}
                                    aria-label="Positive"
                                  />
                                </th>
                                <th className={`${TH} text-right`}>
                                  <ThumbsDown
                                    className="ml-auto h-3.5 w-3.5"
                                    strokeWidth={1.75}
                                    aria-label="Negative"
                                  />
                                </th>
                                <th className={`${TH} text-right`}>Satisfaction</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quality.feedback_by_department.map((r) => (
                                <tr key={r.department} className={TR}>
                                  <td className={TD}>{r.department}</td>
                                  <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                    {num(r.thumbs_up)}
                                  </td>
                                  <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                    {num(r.thumbs_down)}
                                  </td>
                                  <td
                                    className={`${TD} text-right tabular-nums font-medium`}
                                    style={{ color: statusColor(r.satisfaction_rate) }}
                                  >
                                    {pct(r.satisfaction_rate)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lens 2 — model judgement (LLM-as-judge + eval scores) */}
                  <Module title="Model judgement" className="lg:pl-6">
                    {!quality.langfuse_available ? (
                      <p className="text-[13px] text-[#7B6B72]">
                        Model-graded scores need observability enabled — feedback above is
                        unaffected.
                      </p>
                    ) : quality.scores.length === 0 ? (
                      <p className="text-[13px] text-[#7B6B72]">No scores recorded in this window.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#EDE5D8] text-left">
                              <th className={TH}>Score</th>
                              <th className={TH}>Type</th>
                              <th className={`${TH} text-right`}>Mean</th>
                              <th className={`${TH} text-right`}>Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quality.scores.map((s) => (
                              <tr key={s.name} className={TR}>
                                <td className={TD}>{s.name}</td>
                                <td className={`${TD} text-[#7B6B72]`}>{s.data_type}</td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {s.data_type === 'BOOLEAN' ? pct(s.mean) : dec(s.mean, 3)}
                                </td>
                                <td className={`${TD} text-right tabular-nums text-[#3F3338]`}>
                                  {num(s.count)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Module>
                </div>

                {/* Lens 3 — metric validation vs ground truth (full width) */}
                <div className="mt-6 border-t border-[#EDE5D8] pt-6">
                  <MetricValidationCard
                    mv={quality.metric_validation ?? EMPTY_VALIDATION}
                    bars={validationBars}
                    langfuseAvailable={quality.langfuse_available}
                  />
                </div>
              </Section>
            )}

            {/* Operations */}
            {opsLoading && (
              <Section
                title="Operational performance"
                description="The reliability detail behind the Overview headline — latency distribution, control health, and compute drawn down."
              >
                <div className="flex items-center py-6 text-[13px] text-[#7B6B72]">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading operational metrics…
                </div>
              </Section>
            )}
            {!opsLoading && opsError && (
              <Section
                title="Operational performance"
                description="The reliability detail behind the Overview headline — latency distribution, control health, and compute drawn down."
              >
                <div className="flex items-center gap-2 rounded-[12px] border border-[#B23A48]/25 bg-[#B23A48]/8 px-4 py-3 text-[13px] text-[#B23A48]">
                  <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
                  {opsError}
                </div>
              </Section>
            )}
            {!opsLoading && !opsError && ops && (
              <Section
                title="Operational performance"
                description="The reliability detail behind the Overview headline — latency distribution, control health, and compute drawn down."
              >
                {/* Run success and total spend are the Overview headlines. This
                    section owns what Overview can't: latency spread, per-node
                    control health, token draw, and cost split by model. */}
                <KpiGrid cols="grid-cols-2 md:grid-cols-4">
                  <Tile label="Latency p50" value={ms(ops.latency_p50_ms)} icon={Timer} />
                  <Tile label="Latency p95" value={ms(ops.latency_p95_ms)} icon={Timer} />
                  <Tile
                    label="Controls OK"
                    value={pct(ops.controls_pass_rate)}
                    sub="completed vs errored"
                    icon={CheckCircle2}
                    status={statusLevel(ops.controls_pass_rate)}
                  />
                  <Tile
                    label="Tokens"
                    value={num(ops.total_tokens)}
                    sub={`${num(ops.total_calls)} LLM calls`}
                    icon={Gauge}
                  />
                </KpiGrid>
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <Module title="Automated controls — pass rate by node">
                    <HBars data={controlBars} format={(v) => pct(v)} domainMax={1} />
                    <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#7B6B72]">
                      <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHG.good }} />
                        ≥90%
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHG.warn }} />
                        70–90%
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHG.bad }} />
                        &lt;70%
                      </span>
                      <span className="text-[#A8989F]">share of terminal node runs</span>
                    </p>
                  </Module>
                  <Module title="Compute cost by model">
                    {costBars.length === 0 ? (
                      <p className="text-[13px] text-[#7B6B72]">No LLM usage in this window.</p>
                    ) : (
                      <HBars data={costBars} format={(v) => usd(v)} />
                    )}
                  </Module>
                </div>
              </Section>
            )}
          </>
        )}

        {/* Recent sessions — Langfuse-sourced, driven by the top window +
            department/user filter; renders regardless of the aggregate load state. */}
        <SessionsSection
          days={days}
          filter={filter}
          usersById={usersById}
          onPickUser={addUser}
          totalCostUsd={ops?.total_cost_usd}
          totalCostLoading={opsLoading}
        />
      </div>
    </div>
  );
}
